import { cn } from "@/lib/utils";

export type IdentityStage =
  | "not_verified"
  | "pending_backfill"
  | "verified"
  | "duplicate"
  | "unrecoverable"
  | "failed";

interface Props {
  stage: IdentityStage;
  collidingBusinessIds?: string[]; // present for "duplicate" stage; passed through to onClick consumers, NOT used to determine rendering
  size?: "sm" | "md"; // sm for list column, md for detail card
  onClick?: () => void; // wired only when stage === "duplicate"
  className?: string;
}

const STAGE_CONFIG: Record<IdentityStage, { label: string; classes: string }> =
  {
    not_verified: {
      label: "Unverified",
      classes: "bg-muted text-muted-foreground",
    },
    pending_backfill: {
      label: "Legacy",
      classes: "bg-amber-100 text-amber-700",
    },
    verified: { label: "Verified", classes: "bg-green-100 text-green-700" },
    duplicate: { label: "Duplicate", classes: "bg-red-100 text-red-700" },
    unrecoverable: {
      label: "Unrecoverable",
      classes: "bg-amber-100 text-amber-700",
    },
    failed: { label: "Retry pending", classes: "bg-amber-100 text-amber-700" },
  };

const SIZE_CLASSES = {
  sm: "px-2 py-0.5 text-[11px]",
  md: "px-2.5 py-0.5 text-xs",
} as const;

export function IdentityStageBadge({
  stage,
  size = "md",
  onClick,
  className,
}: Props) {
  // eslint-disable-next-line security/detect-object-injection -- controlled IdentityStage type
  const config = STAGE_CONFIG[stage];
  const isClickable = stage === "duplicate" && Boolean(onClick);

  const sharedClasses = cn(
    "inline-flex items-center rounded-full font-semibold",
    config.classes,
    // eslint-disable-next-line security/detect-object-injection -- controlled "sm" | "md" union
    SIZE_CLASSES[size],
    isClickable && "cursor-pointer transition hover:brightness-95",
    className,
  );

  if (isClickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={sharedClasses}
        aria-label={`${config.label} — click to view collision details`}
      >
        {config.label}
      </button>
    );
  }

  return <span className={sharedClasses}>{config.label}</span>;
}
